import React, { useEffect, useCallback } from 'react';
import '../../styles/Modal.css';

/**
 * Aesthetic Double-Bezel Modal Primitive
 * Features:
 * - Obsidian Glassmorphism with Specular Highlight Reflection
 * - Squircle Concentric Radius
 * - Smooth Spring Entry Animations
 * - Keyboard (Escape) Dismissal & Accessibility role
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = 520,
  actions,
  className = '',
}) {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className={`modal-container ${className}`}
        style={{ maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-bar">
          <div className="modal-title-group">
            {title && (
              <h3 id="modal-title" className="modal-title-text">
                {title}
              </h3>
            )}
            {subtitle && <p className="modal-subtitle-text">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="modal-close-pill"
            onClick={onClose}
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        <div className="modal-body-scroll">{children}</div>

        {actions && <div className="modal-footer-actions">{actions}</div>}
      </div>
    </div>
  );
}
